import React, { useState, useMemo } from 'react';
import { C, R, SHADOW, fmt } from '../tokens.js';
import { D } from '../mock-data.js';
import { BkHdr, Card, Btn, Badge, Chip, Stepper, ImgOrEmoji, Divider } from '../components/ui.jsx';
import { useAction } from '../lib/hooks.js';
import { humanizeError } from '../lib/errors.js';
import { createRoutine } from '../api/subscriptions.js';
import { initialChargePaise, paymentCycleLabel, paymentCycleDescription, cycleRechargeSuffix } from '../lib/payment.js';

const WINDOWS = ['6–8 AM', '7–9 AM', '8–10 AM', '4–6 PM', '6–8 PM'];

const monthlyMultiplier = (it) => {
  if (it.cadence === 'Daily') return 30;
  if (it.cadence === 'Alt-day') return 15;
  if (it.cadence === 'Weekly') return 4;
  if (it.cadence === 'Custom' && it.customDays) {
    const days = Object.values(it.customDays).filter(Boolean).length;
    return days * 4;
  }
  return 30;
};

const branchPaymentCycle = (branchId) => D.branches.grocery.find((b) => b.id === branchId)?.paymentCycle || 'per_delivery';

export const RoutineReviewScreen = ({ basket, updateItem, removeItem, clearBasket, addresses, setSubscriptions, navigate, goBack, resetStack, setActiveTab }) => {
  const [window, setWindow] = useState('6–8 AM');
  const [selAddr, setSelAddr] = useState(() => addresses?.find((a) => a.isDefault) || addresses?.[0] || null);
  const [paymentMode, setPaymentMode] = useState('UPI');
  const [editingCadenceFor, setEditingCadenceFor] = useState(null);
  const [success, setSuccess] = useState(null);
  const createAction = useAction(createRoutine);

  // Group items by vendor for display.
  const groups = useMemo(() => {
    const m = new Map();
    for (const it of basket.items) {
      if (!m.has(it.branchId)) m.set(it.branchId, { branchId: it.branchId, branchName: it.branchName, branchRating: it.branchRating, items: [] });
      m.get(it.branchId).items.push(it);
    }
    return Array.from(m.values());
  }, [basket.items]);

  // Detect window mismatches per vendor (from mock branch.windows).
  const branchWindows = (branchId) => {
    const b = D.branches.grocery.find((x) => x.id === branchId);
    return b?.windows || WINDOWS;
  };
  const vendorMismatch = (branchId) => !branchWindows(branchId).includes(window);

  const itemMonthly = (it) => it.pricePaise * it.qty * monthlyMultiplier(it);
  const vendorMonthly = (g) => g.items.reduce((a, it) => a + itemMonthly(it), 0);
  const grandMonthly = groups.reduce((a, g) => a + vendorMonthly(g), 0);
  const itemInitial = (it) => initialChargePaise(branchPaymentCycle(it.branchId), it.pricePaise, it.qty, it.cadence, it.customDays);
  const vendorInitial = (g) => g.items.reduce((a, it) => a + itemInitial(it), 0);
  const grandInitial = groups.reduce((a, g) => a + vendorInitial(g), 0);
  const subCount = basket.items.length;
  const vendorCount = groups.length;

  const handleSubmit = async () => {
    try {
      const res = await createAction.run({
        items: basket.items.map((it) => ({
          branchId: it.branchId,
          branchName: it.branchName,
          productId: it.productId,
          productName: it.name,
          productEm: it.em,
          variantLabel: it.variantLabel,
          qty: it.qty,
          cadence: it.cadence,
          customDays: it.customDays,
          weeklyDay: it.weeklyDay,
          deliveryWindow: window,
          pricePaise: it.pricePaise,
        })),
        deliveryWindow: window,
        addressId: selAddr?.id,
        paymentMode,
      });
      if (res?.subscriptions?.length) {
        setSubscriptions((prev) => [...res.subscriptions, ...prev]);
      }
      clearBasket();
      setSuccess({ count: res?.subscriptions?.length || 0, vendors: vendorCount });
    } catch (e) {
      // error displayed inline below CTA
    }
  };

  if (success) {
    return (
      <div className="fade-in" style={{ flex: 1, display: 'flex', flexDirection: 'column', background: C.bg }}>
        <BkHdr title="Routine started" onBack={() => { resetStack(); setActiveTab('subscriptions'); }} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 72, marginBottom: 18 }}>✅</div>
          <p style={{ fontSize: 22, fontWeight: 800, color: C.text, marginBottom: 8 }}>Routine started</p>
          <p style={{ fontSize: 14, color: C.textMed, lineHeight: 1.5, marginBottom: 24 }}>
            {success.count} subscription{success.count > 1 ? 's' : ''} across {success.vendors} vendor{success.vendors > 1 ? 's' : ''}.<br />
            First deliveries arrive tomorrow morning.
          </p>
          <Btn full onClick={() => { resetStack(); setActiveTab('subscriptions'); }}>See your routine →</Btn>
        </div>
      </div>
    );
  }

  if (basket.items.length === 0) {
    return (
      <div className="fade-in" style={{ flex: 1, display: 'flex', flexDirection: 'column', background: C.bg }}>
        <BkHdr title="Your routine" onBack={goBack} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📅</div>
          <p style={{ fontSize: 16, fontWeight: 700, color: C.text }}>Basket is empty</p>
          <p style={{ fontSize: 13, color: C.textMed, marginTop: 4, marginBottom: 24 }}>Add products to start building your routine</p>
          <Btn onClick={goBack}>Browse products</Btn>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in" style={{ flex: 1, display: 'flex', flexDirection: 'column', background: C.bg }}>
      <BkHdr title="Your routine" onBack={goBack} />

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px 24px' }}>
        {/* Time window picker */}
        <Card style={{ padding: 14, marginBottom: 12 }}>
          <p style={{ fontSize: 13, fontWeight: 800, color: C.text, marginBottom: 10 }}>Delivery window</p>
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', scrollbarWidth: 'none', paddingBottom: 2 }}>
            {WINDOWS.map((w) => (
              <Chip key={w} active={window === w} onClick={() => setWindow(w)}>{w}</Chip>
            ))}
          </div>
          <p style={{ fontSize: 11, color: C.textMed, marginTop: 8 }}>One window across all vendors. Mismatches are flagged below.</p>
        </Card>

        {/* Vendor sections */}
        {groups.map((g) => {
          const mismatch = vendorMismatch(g.branchId);
          return (
            <Card key={g.branchId} style={{ padding: 14, marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10, gap: 8 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <button onClick={() => { const full = D.branches.grocery.find((x) => x.id === g.branchId); if (full) navigate('branch', { branch: full, btype: 'grocery' }); }}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', maxWidth: '100%' }}>
                    <p style={{ fontSize: 14, fontWeight: 800, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      🏬 {g.branchName} <span style={{ fontWeight: 600, color: C.textMed, fontSize: 12 }}>· ⭐{g.branchRating}</span> <span style={{ color: C.primary, fontSize: 11, fontWeight: 700 }}>· View store →</span>
                    </p>
                  </button>
                  {mismatch && (
                    <p style={{ fontSize: 11, color: C.warning, marginTop: 3 }}>⚠ Doesn't deliver {window} — supported: {branchWindows(g.branchId).join(', ')}</p>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                  <Badge bg="#e6f7fb" color={C.primary} sm>💳 {paymentCycleLabel(branchPaymentCycle(g.branchId))}</Badge>
                  <Badge bg={C.successBg} color={C.success} sm>Free delivery</Badge>
                </div>
              </div>
              {g.items.map((it) => (
                <div key={it.productId + ':' + it.variantLabel} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 0', borderTop: `1px solid ${C.border}` }}>
                  <ImgOrEmoji src={null} fallback={it.em} size={44} radius={R.lg} bg="#e6f7fb" fontSize={22} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6, marginBottom: 4 }}>
                      <p style={{ fontSize: 13, fontWeight: 700, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.name}</p>
                      <button onClick={() => removeItem(it.productId, it.variantLabel)} style={{ background: 'none', border: 'none', color: C.textLight, fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: 0, flexShrink: 0 }}>×</button>
                    </div>
                    <p style={{ fontSize: 11, color: C.textMed, marginBottom: 8 }}>{it.variantLabel} · {fmt(it.pricePaise)} / delivery</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <Stepper value={it.qty} onChange={(v) => updateItem(it.productId, it.variantLabel, { qty: v })} />
                      <button onClick={() => setEditingCadenceFor(editingCadenceFor === it.productId + ':' + it.variantLabel ? null : it.productId + ':' + it.variantLabel)}
                        style={{ padding: '6px 12px', borderRadius: R.pill, border: `1.5px solid ${C.border}`, background: C.white, color: C.text, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                        📅 {it.cadence} ▾
                      </button>
                    </div>
                    {editingCadenceFor === it.productId + ':' + it.variantLabel && (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {(it.cadences || ['Daily', 'Alt-day', 'Weekly', 'Custom']).map((c) => (
                            <Chip key={c} active={it.cadence === c}
                              onClick={() => {
                                const patch = { cadence: c };
                                // Initialise customDays only when entering Custom for the first time.
                                if (c === 'Custom' && !it.customDays) {
                                  patch.customDays = { Mon: true, Tue: false, Wed: true, Thu: false, Fri: true, Sat: false, Sun: false };
                                }
                                updateItem(it.productId, it.variantLabel, patch);
                                if (c !== 'Custom') setEditingCadenceFor(null);
                              }}>
                              {c}
                            </Chip>
                          ))}
                        </div>
                        {it.cadence === 'Custom' && (
                          <div style={{ marginTop: 10 }}>
                            <p style={{ fontSize: 10, fontWeight: 800, color: C.textMed, marginBottom: 6, letterSpacing: 0.4 }}>DELIVERY DAYS</p>
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => {
                                const on = !!(it.customDays && it.customDays[d]);
                                return (
                                  <button key={d}
                                    onClick={() => updateItem(it.productId, it.variantLabel, { customDays: { ...(it.customDays || {}), [d]: !on } })}
                                    style={{ minWidth: 36, padding: '6px 8px', borderRadius: R.sm, border: `1.5px solid ${on ? C.primary : C.border}`, background: on ? '#e6f7fb' : C.white, color: on ? C.primary : C.textMed, fontSize: 11, fontWeight: 800, cursor: 'pointer' }}>
                                    {d}
                                  </button>
                                );
                              })}
                            </div>
                            <p style={{ fontSize: 10, color: C.textMed, marginTop: 6 }}>
                              Delivers {Object.values(it.customDays || {}).filter(Boolean).length} day{Object.values(it.customDays || {}).filter(Boolean).length !== 1 ? 's' : ''} a week
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <div style={{ paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
                <p style={{ fontSize: 10, color: C.textMed, marginBottom: 6, lineHeight: 1.4 }}>{paymentCycleDescription(branchPaymentCycle(g.branchId))}</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: 12, color: C.textMed, fontWeight: 600 }}>Pay now to this vendor</span>
                  <span style={{ fontSize: 15, fontWeight: 800, color: C.primary }}>{fmt(vendorInitial(g))}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3 }}>
                  <span style={{ fontSize: 11, color: C.textLight }}>Est. monthly value</span>
                  <span style={{ fontSize: 11, color: C.textMed, fontWeight: 600 }}>{fmt(vendorMonthly(g))}</span>
                </div>
              </div>
            </Card>
          );
        })}

        {/* Address */}
        <Card style={{ padding: 14, marginBottom: 12 }}>
          <p style={{ fontSize: 13, fontWeight: 800, color: C.text, marginBottom: 10 }}>Deliver to</p>
          {addresses && addresses.length > 0 ? (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {addresses.map((a) => (
                <button key={a.id} onClick={() => setSelAddr(a)}
                  style={{
                    padding: '8px 12px', borderRadius: R.md,
                    border: `1.5px solid ${selAddr?.id === a.id ? C.primary : C.border}`,
                    background: selAddr?.id === a.id ? '#e6f7fb' : C.white,
                    color: C.text, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                  {a.label} {a.isDefault && <span style={{ fontSize: 10, color: C.textMed, fontWeight: 500 }}>(default)</span>}
                </button>
              ))}
            </div>
          ) : (
            <p style={{ fontSize: 12, color: C.textMed }}>No saved addresses</p>
          )}
          {selAddr && <p style={{ fontSize: 11, color: C.textMed, marginTop: 8, lineHeight: 1.5 }}>{selAddr.full}</p>}
        </Card>

        {/* Payment */}
        <Card style={{ padding: 14, marginBottom: 12 }}>
          <p style={{ fontSize: 13, fontWeight: 800, color: C.text, marginBottom: 10 }}>Payment mode</p>
          {['UPI', 'COD-per-delivery'].map((p) => (
            <button key={p} onClick={() => setPaymentMode(p)}
              style={{
                width: '100%', padding: '12px 14px', borderRadius: R.md,
                border: `1.5px solid ${paymentMode === p ? C.primary : C.border}`,
                background: paymentMode === p ? '#e6f7fb' : C.white,
                color: C.text, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, textAlign: 'left',
              }}>
              <div style={{ width: 18, height: 18, borderRadius: 9, border: `2px solid ${paymentMode === p ? C.primary : C.border}`, background: C.white, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {paymentMode === p && <div style={{ width: 9, height: 9, borderRadius: 5, background: C.primary }} />}
              </div>
              {p === 'UPI' ? 'UPI · auto-charge before each delivery' : 'Cash on delivery (per delivery)'}
            </button>
          ))}
        </Card>

        {/* Pay-now + monthly estimate */}
        <Card style={{ padding: 14, marginBottom: 12, background: '#e6f7fb', border: `1px solid ${C.primaryLight}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>You pay now</span>
            <span style={{ fontSize: 22, fontWeight: 800, color: C.primary }}>{fmt(grandInitial)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
            <span style={{ fontSize: 11, color: C.textMed }}>Estimated monthly value</span>
            <span style={{ fontSize: 12, color: C.textMed, fontWeight: 600 }}>{fmt(grandMonthly)}</span>
          </div>
          <p style={{ fontSize: 11, color: C.textMed, lineHeight: 1.45 }}>{subCount} subscription{subCount > 1 ? 's' : ''} across {vendorCount} vendor{vendorCount > 1 ? 's' : ''}. Each vendor sets its own billing cycle (shown above). Pause or skip any day from your routine.</p>
        </Card>

        {createAction.error && (
          <div style={{ padding: '10px 14px', borderRadius: R.md, background: C.errorBg, color: C.error, fontSize: 12, fontWeight: 600, marginBottom: 12 }}>
            {humanizeError(createAction.error)}
          </div>
        )}

        <Btn full disabled={createAction.loading || !selAddr} onClick={handleSubmit}>
          {createAction.loading ? 'Starting routine…' : `Pay ${fmt(grandInitial)} & start routine`}
        </Btn>
        <p style={{ fontSize: 10, color: C.textLight, textAlign: 'center', marginTop: 8, lineHeight: 1.4 }}>{subCount} subscription{subCount > 1 ? 's' : ''} · {vendorCount} vendor{vendorCount > 1 ? 's' : ''}</p>
      </div>
    </div>
  );
};
